import { z } from 'zod';
import type { Labelled } from './common.js';
import { CuidSchema } from './crm.schemas.js';

/**
 * التقويم والحجوزات.
 */

/* ==========================================================================
   الثوابت
   ========================================================================== */

export const CALENDAR_KINDS = ['TEAM', 'RESOURCE'] as const;
export type CalendarKind = (typeof CALENDAR_KINDS)[number];

export interface CalendarKindDef extends Labelled<CalendarKind> {
  description: string;
}

export const CALENDAR_KIND_DEFS: readonly CalendarKindDef[] = [
  {
    key: 'TEAM',
    label: 'تقويم فريق',
    kind: 'info',
    description: 'مواعيد الأشخاص: اجتماعات ومكالمات ومهام. التداخل فيه مقبول.',
  },
  {
    key: 'RESOURCE',
    label: 'تقويم مورد',
    kind: 'brand',
    description:
      'شيء قابل للحجز بسعة محدودة: استوديو أو قاعة أو معدّة. يمنع الحجز المزدوج.',
  },
] as const;

export const CALENDAR_KIND_MAP = Object.fromEntries(
  CALENDAR_KIND_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<CalendarKind, CalendarKindDef>>;

export const EVENT_STATUSES = ['TENTATIVE', 'CONFIRMED', 'CANCELLED'] as const;
export type EventStatus = (typeof EVENT_STATUSES)[number];

export const EVENT_STATUS_DEFS: readonly Labelled<EventStatus>[] = [
  { key: 'TENTATIVE', label: 'مبدئي', kind: 'warning' },
  { key: 'CONFIRMED', label: 'مؤكَّد', kind: 'success' },
  { key: 'CANCELLED', label: 'ملغى', kind: 'danger' },
] as const;

export const EVENT_STATUS_MAP = Object.fromEntries(
  EVENT_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<EventStatus, Labelled<EventStatus>>>;

/** أيام الأسبوع بترتيب التقويم العربي: السبت أولًا. */
export const WEEK_DAYS = [
  { key: 'sat', label: 'السبت', short: 'س', index: 6 },
  { key: 'sun', label: 'الأحد', short: 'ح', index: 0 },
  { key: 'mon', label: 'الاثنين', short: 'ن', index: 1 },
  { key: 'tue', label: 'الثلاثاء', short: 'ث', index: 2 },
  { key: 'wed', label: 'الأربعاء', short: 'ر', index: 3 },
  { key: 'thu', label: 'الخميس', short: 'خ', index: 4 },
  { key: 'fri', label: 'الجمعة', short: 'ج', index: 5 },
] as const;

export type WeekDayKey = (typeof WEEK_DAYS)[number]['key'];

/** مفتاح اليوم من تاريخ. */
export function weekDayKey(date: Date): WeekDayKey {
  return WEEK_DAYS.find((d) => d.index === date.getDay())!.key;
}

/** ألوان مقترحة للتقاويم، متمايزة بما يكفي عند عرضها معًا. */
export const CALENDAR_COLORS = [
  '#3672EA',
  '#51A521',
  '#BF930B',
  '#DC2626',
  '#7C3AED',
  '#0891B2',
  '#DB2777',
  '#65A30D',
] as const;

/* ==========================================================================
   حساب التداخل
   ========================================================================== */

export interface TimeRange {
  startAt: Date | string;
  endAt: Date | string;
}

/**
 * هل يتداخل مداان زمنيان؟
 *
 * النهاية غير شاملة عن قصد: موعد ينتهي الثانية عشرة وآخر يبدأ الثانية عشرة
 * لا يتداخلان. المعاملة المخالفة تمنع حجز فترتين متتاليتين مباشرة، وهو ما
 * يحدث كثيرًا في حجز الاستوديوهات.
 */
export function overlaps(a: TimeRange, b: TimeRange): boolean {
  const aStart = new Date(a.startAt).getTime();
  const aEnd = new Date(a.endAt).getTime();
  const bStart = new Date(b.startAt).getTime();
  const bEnd = new Date(b.endAt).getTime();
  return aStart < bEnd && bStart < aEnd;
}

export interface WorkingHours {
  workingDays: readonly string[];
  workStart: string;
  workEnd: string;
}

/** يحوّل "09:30" إلى دقائق من منتصف الليل. */
export function minutesOfDay(time: string): number {
  const [h, m] = time.split(':');
  return Number(h) * 60 + Number(m ?? 0);
}

export interface WorkingHoursCheck {
  ok: boolean;
  reason?: string;
}

/**
 * هل يقع الموعد ضمن ساعات العمل المعرَّفة للتقويم؟
 *
 * الفحص تحذيري لا مانع: الحجز خارج ساعات العمل يحدث فعلًا — تصوير ليلي أو
 * اجتماع في إجازة — والمنع الصارم يدفع المستخدم إلى تعطيل الميزة كلها.
 */
export function withinWorkingHours(
  range: TimeRange,
  hours: WorkingHours,
): WorkingHoursCheck {
  const start = new Date(range.startAt);
  const end = new Date(range.endAt);

  const day = weekDayKey(start);
  if (!hours.workingDays.includes(day)) {
    const label = WEEK_DAYS.find((d) => d.key === day)?.label ?? day;
    return { ok: false, reason: `${label} ليس من أيام العمل المعرَّفة لهذا التقويم.` };
  }

  const startMinutes = start.getHours() * 60 + start.getMinutes();
  const endMinutes = end.getHours() * 60 + end.getMinutes();
  const openAt = minutesOfDay(hours.workStart);
  const closeAt = minutesOfDay(hours.workEnd);

  if (startMinutes < openAt || endMinutes > closeAt) {
    return {
      ok: false,
      reason: `ساعات العمل من ${hours.workStart} إلى ${hours.workEnd}.`,
    };
  }

  return { ok: true };
}

/** مدة الموعد بالدقائق. */
export function durationMinutes(range: TimeRange): number {
  return Math.round(
    (new Date(range.endAt).getTime() - new Date(range.startAt).getTime()) / 60_000,
  );
}

/* ==========================================================================
   مخططات التحقق
   ========================================================================== */

const optionalText = (max = 500) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

export const CreateCalendarSchema = z.object({
  name: z.string().trim().min(2, 'اسم التقويم مطلوب.').max(80),
  kind: z.enum(CALENDAR_KINDS).default('TEAM'),
  description: optionalText(300),
  color: z
    .string()
    .regex(/^#[0-9a-f]{6}$/i, 'اللون يجب أن يكون بصيغة ست عشرية.')
    .default('#3672EA'),
  capacity: z.number().int().min(1).max(100).default(1),
  /*
   * بلا قيمة افتراضية عن قصد.
   *
   * القيمة الافتراضية هنا تُملأ قبل وصول الطلب إلى الخادم، فيستحيل التمييز
   * بين "لم يحدّد المستخدم" و"اختار الإطفاء صراحةً". والخادم يحتاج هذا
   * التمييز ليجعل تقويم المورد مانعًا للتداخل ما لم يُطلب غير ذلك.
   */
  preventOverlap: z.boolean().optional(),
  workingDays: z
    .array(z.enum(['sat', 'sun', 'mon', 'tue', 'wed', 'thu', 'fri']))
    .min(1, 'حدد يوم عمل واحدًا على الأقل.')
    .default(['sat', 'sun', 'mon', 'tue', 'wed', 'thu']),
  workStart: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'صيغة الوقت غير صحيحة.').default('09:00'),
  workEnd: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'صيغة الوقت غير صحيحة.').default('18:00'),
  ownerId: CuidSchema.nullable().optional(),
});
export type CreateCalendarInput = z.infer<typeof CreateCalendarSchema>;

export const UpdateCalendarSchema = CreateCalendarSchema.partial().extend({
  isActive: z.boolean().optional(),
});
export type UpdateCalendarInput = z.infer<typeof UpdateCalendarSchema>;

export const CreateEventSchema = z
  .object({
    calendarId: CuidSchema,
    title: z.string().trim().min(2, 'عنوان الموعد مطلوب.').max(200),
    description: optionalText(2000),
    location: optionalText(200),
    startAt: z.coerce.date(),
    endAt: z.coerce.date(),
    allDay: z.boolean().default(false),
    status: z.enum(EVENT_STATUSES).default('CONFIRMED'),
    color: z
      .string()
      .regex(/^#[0-9a-f]{6}$/i)
      .nullable()
      .optional(),
    dealId: CuidSchema.nullable().optional(),
    accountId: CuidSchema.nullable().optional(),
    leadId: CuidSchema.nullable().optional(),
    contactId: CuidSchema.nullable().optional(),
    attendees: z.array(z.string().trim().max(200)).max(50).default([]),
    meetingProvider: optionalText(40),
    meetingUrl: optionalText(500),
    /** تجاهل تحذير خارج ساعات العمل والمضي في الحجز. */
    force: z.boolean().default(false),
  })
  .refine((v) => v.endAt > v.startAt, {
    message: 'وقت الانتهاء يجب أن يلي وقت البدء.',
    path: ['endAt'],
  });
export type CreateEventInput = z.infer<typeof CreateEventSchema>;

export const UpdateEventSchema = z
  .object({
    calendarId: CuidSchema.optional(),
    title: z.string().trim().min(2).max(200).optional(),
    description: optionalText(2000),
    location: optionalText(200),
    startAt: z.coerce.date().optional(),
    endAt: z.coerce.date().optional(),
    allDay: z.boolean().optional(),
    status: z.enum(EVENT_STATUSES).optional(),
    color: z.string().regex(/^#[0-9a-f]{6}$/i).nullable().optional(),
    dealId: CuidSchema.nullable().optional(),
    accountId: CuidSchema.nullable().optional(),
    leadId: CuidSchema.nullable().optional(),
    attendees: z.array(z.string().trim().max(200)).max(50).optional(),
    meetingProvider: optionalText(40),
    meetingUrl: optionalText(500),
    force: z.boolean().default(false),
  })
  .refine((v) => !v.startAt || !v.endAt || v.endAt > v.startAt, {
    message: 'وقت الانتهاء يجب أن يلي وقت البدء.',
    path: ['endAt'],
  });
export type UpdateEventInput = z.infer<typeof UpdateEventSchema>;

export const EventRangeSchema = z.object({
  from: z.coerce.date(),
  to: z.coerce.date(),
  /** تقاويم بعينها، أو الكل عند الإغفال. */
  calendarIds: z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform((v) => (v === undefined ? undefined : Array.isArray(v) ? v : v.split(','))),
  status: z.enum(EVENT_STATUSES).optional(),
});
export type EventRangeQuery = z.infer<typeof EventRangeSchema>;

export const AvailabilitySchema = z.object({
  calendarId: CuidSchema,
  startAt: z.coerce.date(),
  endAt: z.coerce.date(),
  /** يُستثنى عند تعديل موعد قائم، فلا يتعارض مع نفسه. */
  excludeEventId: CuidSchema.optional(),
});
export type AvailabilityQuery = z.infer<typeof AvailabilitySchema>;
